import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, ListChecks, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';

// ── CompleteRequiredFieldsModal ───────────────────────────────────────────────
//
// The fill-in-place step of the confirm flow: when the push-readiness check
// says the action would fail (missing product, contract term, advisor, sales
// rep…), this modal collects EXACTLY those fields where the user confirms the
// action — instead of a raw 422 after the click, or worse, a partial write.
// (Aidan 2026-07-10: "if there are required fields missing for that action,
// it should populate them in the modal where you confirm the action".)
//
// Props:
//   open              {boolean}
//   onOpenChange      {(boolean) => void}
//   readiness         {object|null} — the /push-readiness response
//   actionLabel       {string}      — e.g. "Confirm Match", "Create Order"
//   providerProducts  {[{id, name}]} — the row's product options
//   catalogWarning    {string|null} — truthful reason the catalog is empty
//                     (auth failure / fetch error), shown in place of the
//                     "set one on the template" message when set
//   salesReps         {{advisor, items}|null} — drawer cache, null = loading
//   submitting        {boolean}
//   onConfirm         {(fixes) => Promise<void>} — fixes keyed by missing.key:
//                     product → {id, name}; sales_rep → {id, name};
//                     contract_term / advisor_company / customer_name → string

export default function CompleteRequiredFieldsModal({
  open,
  onOpenChange,
  readiness,
  actionLabel,
  providerProducts = [],
  catalogWarning = null,
  salesReps = null,
  submitting,
  onConfirm,
}) {
  const missing = useMemo(() => readiness?.missing || [], [readiness]);
  const autofill = readiness?.autofill || {};

  const [values, setValues] = useState({});
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [repPopoverOpen, setRepPopoverOpen] = useState(false);
  const [advisorPopoverOpen, setAdvisorPopoverOpen] = useState(false);
  const [advisorQuery, setAdvisorQuery] = useState('');
  const [advisorItems, setAdvisorItems] = useState([]);
  // 'idle' (popover open, nothing typed) | 'loading' | 'success' | 'error' —
  // kept distinct so a dead session/failed fetch never renders identically
  // to "no matches" (see friendlyFailure() in PushHistorySection.js for the
  // sibling pattern on the AppDirectCall side of this module).
  const [advisorStatus, setAdvisorStatus] = useState('idle');
  const [advisorErrorMessage, setAdvisorErrorMessage] = useState('');

  useEffect(() => {
    if (open) setValues({});
  }, [open, readiness]);

  // Debounced live roster search — same endpoint the create-customer picker
  // uses (free-typed advisors that aren't in the roster can't book). Fires
  // on 1+ characters — GetAdvisors can't be enumerated blindly, but a single
  // letter is a valid, useful query; the backend sorts + caps the roster.
  useEffect(() => {
    const query = advisorQuery.trim();
    if (!advisorPopoverOpen || !query) {
      setAdvisorItems([]);
      setAdvisorStatus('idle');
      setAdvisorErrorMessage('');
      return undefined;
    }
    let alive = true;
    setAdvisorStatus('loading');
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `${API_URL}/api/match/appdirect/advisors?name=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        if (!alive) return;
        if (res.ok) {
          setAdvisorItems(data?.items ?? []);
          setAdvisorStatus('success');
        } else {
          setAdvisorItems([]);
          setAdvisorStatus('error');
          setAdvisorErrorMessage(data?.detail || `Advisor search failed (HTTP ${res.status}).`);
        }
      } catch (err) {
        if (!alive) return;
        setAdvisorItems([]);
        setAdvisorStatus('error');
        setAdvisorErrorMessage(err?.message || 'Advisor search failed unexpectedly.');
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [advisorQuery, advisorPopoverOpen]);

  function set(key, value) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  // settings-kind items (e.g. the AppDirect cookie) can't be fixed from this
  // modal — they render as a message and keep Confirm disabled.
  const allFilled = missing.every((m) => {
    if (m.kind === 'settings') return false;
    const v = values[m.key];
    if (m.kind === 'product' || m.kind === 'sales_rep') return v && v.id;
    return typeof v === 'string' && v.trim() !== '';
  });

  async function handleConfirm() {
    if (!allFilled) return;
    await onConfirm(values);
  }

  function renderInput(item) {
    const v = values[item.key];
    switch (item.kind) {
      case 'settings':
        return (
          <div
            className="rounded-md border border-[var(--boss-warning-border)] bg-[var(--boss-warning-soft)] px-3 py-2 text-[12px] leading-snug text-[var(--boss-warning-text)]"
            data-testid="complete-fields-settings-note"
          >
            This needs an admin fix in Settings — it can’t be completed from here.
          </div>
        );
      case 'product':
        return (
          <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={productPopoverOpen}
                data-testid="complete-fields-product-input"
                className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!v?.name ? 'text-boss-text-muted' : ''}`}
              >
                <span className="truncate">{v?.name || 'Pick a product…'}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[380px] p-0 bg-boss-surface border border-boss-border text-boss-text"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder="Search products…"
                  className="h-9 text-sm text-boss-text bg-boss-surface"
                />
                <CommandList>
                  <CommandEmpty
                    className={`py-4 text-center text-sm ${
                      catalogWarning && providerProducts.length === 0
                        ? 'text-[var(--boss-warning-text)]'
                        : 'text-boss-text-muted'
                    }`}
                    data-testid="complete-fields-product-empty"
                  >
                    {providerProducts.length === 0
                      ? (catalogWarning || 'No products on this row — set one on the template.')
                      : 'No products match.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {providerProducts.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={p.name}
                        onSelect={() => {
                          set(item.key, { id: p.id, name: p.name });
                          setProductPopoverOpen(false);
                        }}
                        className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated"
                      >
                        <Check
                          className={`mr-2 h-4 w-4 shrink-0 ${v?.id === p.id ? 'opacity-100' : 'opacity-0'}`}
                        />
                        <span className="truncate">{p.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        );

      case 'date':
        return (
          <Input
            type="date"
            className="boss-input"
            value={v || ''}
            onChange={(e) => set(item.key, e.target.value)}
            data-testid={`complete-fields-${item.key}-input`}
          />
        );

      case 'term':
        return (
          <Select value={v || ''} onValueChange={(val) => set(item.key, val)}>
            <SelectTrigger
              className="boss-input w-full"
              data-testid="complete-fields-term-trigger"
            >
              <SelectValue placeholder="Pick a term…" />
            </SelectTrigger>
            <SelectContent className="max-h-[min(280px,var(--radix-select-content-available-height))] overflow-y-auto bg-boss-surface border border-boss-border text-boss-text">
              {(item.options || []).map((opt) => (
                <SelectItem
                  key={opt}
                  value={opt}
                  className="hover:bg-boss-surface-elevated focus:bg-boss-surface-elevated text-boss-text"
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'advisor':
        return (
          <Popover open={advisorPopoverOpen} onOpenChange={setAdvisorPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={advisorPopoverOpen}
                data-testid="complete-fields-advisor-input"
                className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!v ? 'text-boss-text-muted' : ''}`}
              >
                <span className="truncate">{v || 'Search Provider Sales advisors…'}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[380px] p-0 bg-boss-surface border border-boss-border text-boss-text"
              align="start"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Type a letter to browse, or search by name…"
                  value={advisorQuery}
                  onValueChange={setAdvisorQuery}
                  className="h-9 text-sm text-boss-text bg-boss-surface"
                  data-testid="complete-fields-advisor-search-input"
                />
                <CommandList>
                  <CommandEmpty className="py-4 px-3 text-left text-sm">
                    {advisorStatus === 'loading' && (
                      <span className="text-boss-text-muted">Searching…</span>
                    )}
                    {advisorStatus === 'idle' && (
                      <span className="text-boss-text-muted">
                        Type a letter to browse Provider Sales advisors A-Z, or keep typing to narrow it down.
                      </span>
                    )}
                    {advisorStatus === 'error' && (
                      <div
                        className="boss-alert-danger rounded-md px-3 py-2 text-xs leading-snug"
                        data-testid="complete-fields-advisor-error"
                      >
                        Couldn’t reach the advisor roster: {advisorErrorMessage}
                      </div>
                    )}
                    {advisorStatus === 'success' && (
                      <span className="text-boss-text-muted">No advisors found.</span>
                    )}
                  </CommandEmpty>
                  <CommandGroup>
                    {advisorItems.map((a) => (
                      <CommandItem
                        key={a.internal_id}
                        value={a.name}
                        onSelect={() => {
                          set(item.key, a.name);
                          setAdvisorPopoverOpen(false);
                        }}
                        className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated"
                      >
                        <Check
                          className={`mr-2 h-4 w-4 shrink-0 ${v === a.name ? 'opacity-100' : 'opacity-0'}`}
                        />
                        <span className="truncate">{a.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        );

      case 'sales_rep':
        if (salesReps && salesReps.items.length > 0) {
          return (
            <Popover open={repPopoverOpen} onOpenChange={setRepPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={repPopoverOpen}
                  data-testid="complete-fields-sales-rep-input"
                  className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!v?.name ? 'text-boss-text-muted' : ''}`}
                >
                  <span className="truncate">{v?.name || 'Search reps…'}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[320px] p-0 bg-boss-surface border border-boss-border text-boss-text"
                align="start"
              >
                <Command>
                  <CommandInput
                    placeholder="Search reps…"
                    className="h-9 text-sm text-boss-text bg-boss-surface"
                  />
                  <CommandList>
                    <CommandEmpty className="py-4 text-center text-sm text-boss-text-muted">
                      No reps found.
                    </CommandEmpty>
                    <CommandGroup>
                      {salesReps.items.map((rep) => (
                        <CommandItem
                          key={rep.id}
                          value={rep.name}
                          onSelect={() => {
                            set(item.key, { id: rep.id, name: rep.name });
                            setRepPopoverOpen(false);
                          }}
                          className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated"
                        >
                          <Check
                            className={`mr-2 h-4 w-4 shrink-0 ${v?.id === rep.id ? 'opacity-100' : 'opacity-0'}`}
                          />
                          <span className="truncate">{rep.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          );
        }
        return (
          <Input
            className="boss-input"
            value={v?.name || ''}
            readOnly
            placeholder={
              salesReps === null
                ? 'Loading the advisor’s reps…'
                : 'No reps returned for this advisor — fix the advisor first.'
            }
            data-testid="complete-fields-sales-rep-input"
          />
        );

      default:
        return (
          <Input
            className="boss-input"
            value={v || ''}
            onChange={(e) => set(item.key, e.target.value)}
            data-testid={`complete-fields-${item.key}-input`}
          />
        );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] bg-boss-surface border border-boss-border text-boss-text"
        data-testid="complete-fields-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-boss-text">
            <ListChecks className="h-5 w-5 text-[var(--boss-warning-text)]" />
            A few fields are needed first
          </DialogTitle>
          <DialogDescription className="text-boss-text-secondary">
            AppDirect requires these before “{actionLabel}” can book. Fill them
            here and continue — nothing is sent until they’re set.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {missing.map((item) => (
            <div key={item.key}>
              <Label className="text-xs text-boss-text-secondary mb-1.5 block">
                {item.label}
              </Label>
              {renderInput(item)}
              {item.detail && (
                <p className="mt-1 text-[11px] leading-snug text-boss-text-muted">
                  {item.detail}
                </p>
              )}
            </div>
          ))}

          {/* Values BOSS filled in on its own — shown for transparency */}
          {autofill.sales_rep_name && (
            <div
              className="flex items-start gap-2 rounded-md border border-[var(--boss-info-border)] bg-[var(--boss-info-soft)] px-3 py-2"
              data-testid="complete-fields-autofill-note"
            >
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-[var(--boss-info-text)]" />
              <p className="text-[12px] leading-snug text-[var(--boss-info-text)]">
                Sales Rep <strong>{autofill.sales_rep_name}</strong> was
                auto-filled from the {autofill.sales_rep_source || 'confirmed match'}.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-boss-border text-boss-text-secondary hover:text-boss-text hover:bg-boss-elevated"
            data-testid="complete-fields-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || !allFilled}
            className="btn-primary"
            data-testid="complete-fields-confirm-button"
          >
            {submitting ? 'Working…' : `Save & ${actionLabel}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
